/**
 * SurveyBody — конструктор опроса. Словарь вопросов соответствует контракту
 * surveyQuestionSchema из @inbx/shared: идентификатор вопроса — `id`, текст — `text`.
 * Типы: open_text / single_choice / multiple_choice / rating (stars/nps/emoji) /
 * scale (min/max/step + подписи) / email / phone.
 *
 * Branching (условные переходы) намеренно НЕ поддерживается — ни контракт, ни
 * рантайм его не реализуют; держим редактор и рантайм в одном словаре.
 */
import { defaultTemplateMeta } from '@inbx/shared/schemas';
import { type ReactNode } from 'react';

import { type WidgetBodyContract, type WidgetDraft } from './editor-shell';
import { AssetUpload, BehaviorTab, CodeTab, ThemeTab } from './shared-tabs';

type QuestionType =
  | 'open_text'
  | 'single_choice'
  | 'multiple_choice'
  | 'rating'
  | 'scale'
  | 'email'
  | 'phone'
  | 'image_choice';

type RatingType = 'stars' | 'nps' | 'emoji';

interface SurveyQuestion {
  id: string;
  text: string;
  type: QuestionType;
  required?: boolean;
  description?: string;
  placeholder?: string;
  options?: string[];
  rating_type?: RatingType;
  scale_min?: number;
  scale_max?: number;
  scale_step?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  branches?: Array<{ when: { answer: string }; goto: string }>;
  image_options?: Array<{ value: string; image_url: string; label?: string }>;
}

interface SurveyContent {
  title?: string;
  completion_message?: string;
  welcome_screen?: boolean;
  welcome_text?: string;
  show_progress?: boolean;
  allow_back?: boolean;
  questions?: SurveyQuestion[];
}

export function makeSurveyBody(args: {
  widgetPublicKey: string;
  workspaceId: string;
}): WidgetBodyContract {
  return {
    getTabs() {
      return [
        { key: 'content', label: 'Вопросы' },
        { key: 'theme', label: 'Тема' },
        { key: 'behavior', label: 'Поведение' },
        { key: 'code', label: 'Код' },
      ];
    },
    defaultDraft() {
      // Старт с реального пресета из общего реестра (структурно «настоящего»).
      const t = defaultTemplateMeta('survey');
      return {
        themeConfig: { template_id: t.id },
        contentConfig: { ...(t.defaultContent as WidgetDraft['contentConfig']) },
        behaviorConfig: { ...(t.defaultBehavior ?? {}) } as WidgetDraft['behaviorConfig'],
        mediaConfig: {},
      };
    },
    validate(draft) {
      const errs: string[] = [];
      const c = (draft.contentConfig ?? {}) as SurveyContent;
      const questions = c.questions ?? [];
      if (!questions.length) errs.push('Добавьте хотя бы один вопрос');
      for (const q of questions) {
        if (!q.id.trim()) errs.push(`Вопрос "${q.text}" без идентификатора`);
        if (
          (q.type === 'single_choice' || q.type === 'multiple_choice') &&
          (!q.options || q.options.length === 0)
        ) {
          errs.push(`Вопрос "${q.text}" нуждается в вариантах ответа`);
        }
      }
      return errs;
    },
    renderTab(activeKey, draft, onChange): ReactNode {
      if (activeKey === 'content') return <SurveyQuestionsTab draft={draft} onChange={onChange} />;
      if (activeKey === 'theme') return <ThemeTab draft={draft} onChange={onChange} />;
      if (activeKey === 'behavior') return <BehaviorTab draft={draft} onChange={onChange} />;
      if (activeKey === 'code')
        return <CodeTab widgetPublicKey={args.widgetPublicKey} workspaceId={args.workspaceId} />;
      return null;
    },
  };
}

const QUESTION_TYPES: Array<{ value: QuestionType; label: string }> = [
  { value: 'open_text', label: 'Свободный ответ' },
  { value: 'single_choice', label: 'Один вариант' },
  { value: 'multiple_choice', label: 'Несколько вариантов' },
  { value: 'rating', label: 'Оценка (звёзды/NPS/эмодзи)' },
  { value: 'scale', label: 'Шкала' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Телефон' },
  { value: 'image_choice', label: 'Выбор картинки' },
];

const RATING_TYPES: Array<{ value: RatingType; label: string }> = [
  { value: 'stars', label: 'Звёзды (1–5)' },
  { value: 'nps', label: 'NPS (0–10)' },
  { value: 'emoji', label: 'Эмодзи (1–5)' },
];

function SurveyQuestionsTab({
  draft,
  onChange,
}: {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
}): JSX.Element {
  const c = (draft.contentConfig ?? {}) as SurveyContent;
  const questions = c.questions ?? [];
  const set = (patch: Partial<SurveyContent>): void =>
    onChange({ contentConfig: { ...draft.contentConfig, ...patch } });
  const setQs = (next: SurveyQuestion[]): void => set({ questions: next });
  const update = (i: number, patch: Partial<SurveyQuestion>): void =>
    setQs(questions.map((q, j) => (i === j ? { ...q, ...patch } : q)));
  const move = (i: number, dir: -1 | 1): void => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setQs(next);
  };
  const remove = (i: number): void => setQs(questions.filter((_, j) => j !== i));
  const add = (): void =>
    setQs([
      ...questions,
      {
        id: `q_${Date.now()}`,
        text: 'Новый вопрос',
        type: 'single_choice',
        options: ['Да', 'Нет'],
      },
    ]);

  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Заголовки</div>
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Заголовок</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.title ?? ''}
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Сообщение после завершения</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.completion_message ?? ''}
              onChange={(e) => set({ completion_message: e.target.value })}
            />
          </div>
        </div>
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={c.welcome_screen ?? false}
            onChange={(e) => set({ welcome_screen: e.target.checked })}
          />
          Показывать приветственный экран
        </label>
        {c.welcome_screen && (
          <div style={{ marginTop: 8 }}>
            <label className="inbx-editor-label">Текст приветствия</label>
            <textarea
              className="inbx-editor-textarea"
              rows={2}
              value={c.welcome_text ?? ''}
              onChange={(e) => set({ welcome_text: e.target.value })}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 20, marginTop: 10 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={c.show_progress ?? false}
              onChange={(e) => set({ show_progress: e.target.checked })}
            />
            Прогресс-бар
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={c.allow_back ?? false}
              onChange={(e) => set({ allow_back: e.target.checked })}
            />
            Разрешить «Назад»
          </label>
        </div>
      </div>

      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Вопросы</div>
        <div className="inbx-editor-help" style={{ marginBottom: 8 }}>
          Перетащите за «⋮⋮» слева чтобы поменять порядок, либо стрелками ↑↓.
        </div>
        {questions.length === 0 && <div className="inbx-editor-help">Нет вопросов — добавьте.</div>}
        {questions.map((q, i) => (
          <QuestionEditor
            key={q.id + i}
            q={q}
            index={i}
            allQuestions={questions}
            onChange={(p) => update(i, p)}
            onMove={(d) => move(i, d)}
            onRemove={() => remove(i)}
            isFirst={i === 0}
            isLast={i === questions.length - 1}
            onDragSwap={(from, to) => {
              if (
                from === to ||
                from < 0 ||
                to < 0 ||
                from >= questions.length ||
                to >= questions.length
              )
                return;
              const next = [...questions];
              const [picked] = next.splice(from, 1);
              if (picked) next.splice(to, 0, picked);
              setQs(next);
            }}
          />
        ))}
        <button type="button" className="inbx-btn" onClick={add} style={{ marginTop: 8 }}>
          + Добавить вопрос
        </button>
      </div>
    </div>
  );
}

function QuestionEditor({
  q,
  index,
  allQuestions,
  onChange,
  onMove,
  onRemove,
  isFirst,
  isLast,
  onDragSwap,
}: {
  q: SurveyQuestion;
  index: number;
  allQuestions: SurveyQuestion[];
  onChange: (patch: Partial<SurveyQuestion>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  isFirst: boolean;
  isLast: boolean;
  onDragSwap: (from: number, to: number) => void;
}): JSX.Element {
  const opts = q.options ?? [];
  const needsOpts = q.type === 'single_choice' || q.type === 'multiple_choice';
  const branches = q.branches ?? [];
  const setBranches = (b: typeof branches): void => onChange({ branches: b });

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (Number.isFinite(from)) onDragSwap(from, index);
      }}
      style={{
        border: '1px solid var(--inbx-border)',
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
        background: 'var(--inbx-bg-secondary)',
        position: 'relative',
        paddingLeft: 28,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 8,
          top: 14,
          color: 'var(--inbx-text-tertiary)',
          cursor: 'grab',
          userSelect: 'none',
          fontSize: 16,
          lineHeight: 1,
        }}
        title="Перетащить"
      >
        ⋮⋮
      </div>
      <div className="inbx-editor-row">
        <div>
          <label className="inbx-editor-label">Текст вопроса</label>
          <input
            type="text"
            className="inbx-editor-input"
            value={q.text}
            onChange={(e) => onChange({ text: e.target.value })}
          />
        </div>
        <div>
          <label className="inbx-editor-label">Идентификатор</label>
          <input
            type="text"
            className="inbx-editor-input"
            value={q.id}
            onChange={(e) => onChange({ id: e.target.value.replace(/[^a-z0-9_]/gi, '_') })}
          />
        </div>
        <div style={{ flex: 0, alignSelf: 'flex-end', display: 'flex', gap: 4 }}>
          <button type="button" className="inbx-btn" disabled={isFirst} onClick={() => onMove(-1)} title="Выше">
            ↑
          </button>
          <button type="button" className="inbx-btn" disabled={isLast} onClick={() => onMove(1)} title="Ниже">
            ↓
          </button>
          <button type="button" className="inbx-btn inbx-btn--danger" onClick={onRemove} title="Удалить">
            ✕
          </button>
        </div>
      </div>

      <div className="inbx-editor-row">
        <div>
          <label className="inbx-editor-label">Тип</label>
          <select
            className="inbx-editor-select"
            value={q.type}
            onChange={(e) => onChange({ type: e.target.value as QuestionType })}
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        {q.type === 'rating' && (
          <div>
            <label className="inbx-editor-label">Вид оценки</label>
            <select
              className="inbx-editor-select"
              value={q.rating_type ?? 'stars'}
              onChange={(e) => onChange({ rating_type: e.target.value as RatingType })}
            >
              {RATING_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {q.type === 'scale' && (
          <>
            <div>
              <label className="inbx-editor-label">От</label>
              <input
                type="number"
                className="inbx-editor-input"
                value={q.scale_min ?? 1}
                onChange={(e) => onChange({ scale_min: Number(e.target.value) || 1 })}
              />
            </div>
            <div>
              <label className="inbx-editor-label">До</label>
              <input
                type="number"
                className="inbx-editor-input"
                value={q.scale_max ?? 5}
                onChange={(e) => onChange({ scale_max: Number(e.target.value) || 5 })}
              />
            </div>
            <div>
              <label className="inbx-editor-label">Шаг</label>
              <input
                type="number"
                className="inbx-editor-input"
                value={q.scale_step ?? 1}
                onChange={(e) => onChange({ scale_step: Number(e.target.value) || 1 })}
              />
            </div>
          </>
        )}
      </div>

      {q.type === 'scale' && (
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Подпись «минимум»</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={q.scale_min_label ?? ''}
              onChange={(e) => onChange({ scale_min_label: e.target.value })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Подпись «максимум»</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={q.scale_max_label ?? ''}
              onChange={(e) => onChange({ scale_max_label: e.target.value })}
            />
          </div>
        </div>
      )}

      <label
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={q.required ?? false}
          onChange={(e) => onChange({ required: e.target.checked })}
        />
        Обязательный
      </label>

      {needsOpts && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--inbx-border)' }}>
          <label className="inbx-editor-label">Варианты ответа</label>
          {opts.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
              <input
                type="text"
                className="inbx-editor-input"
                value={o}
                onChange={(e) =>
                  onChange({ options: opts.map((x, j) => (i === j ? e.target.value : x)) })
                }
              />
              <button
                type="button"
                className="inbx-btn inbx-btn--danger"
                onClick={() => onChange({ options: opts.filter((_, j) => j !== i) })}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="inbx-btn"
            onClick={() => onChange({ options: [...opts, `Вариант ${opts.length + 1}`] })}
          >
            + Опция
          </button>
        </div>
      )}

      {q.type === 'single_choice' && opts.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--inbx-border)' }}>
          <label className="inbx-editor-label">Ветвления (переход по ответу)</label>
          {branches.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
              <span style={{ fontSize: 12 }}>Если</span>
              <select
                className="inbx-editor-select"
                style={{ flex: 1 }}
                value={b.when.answer}
                onChange={(e) =>
                  setBranches(branches.map((x, j) => (j === i ? { ...x, when: { answer: e.target.value } } : x)))
                }
              >
                {opts.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              <span style={{ fontSize: 12 }}>→</span>
              <select
                className="inbx-editor-select"
                style={{ flex: 1 }}
                value={b.goto}
                onChange={(e) =>
                  setBranches(branches.map((x, j) => (j === i ? { ...x, goto: e.target.value } : x)))
                }
              >
                <option value="__end__">Завершить</option>
                {allQuestions
                  .filter((qq) => qq.id !== q.id)
                  .map((qq) => (
                    <option key={qq.id} value={qq.id}>{qq.text || qq.id}</option>
                  ))}
              </select>
              <button
                type="button"
                className="inbx-btn inbx-btn--danger"
                onClick={() => setBranches(branches.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="inbx-btn"
            onClick={() => setBranches([...branches, { when: { answer: opts[0] ?? '' }, goto: '__end__' }])}
          >
            + Условие
          </button>
          <div className="inbx-editor-help">Без условий — переход к следующему вопросу по порядку.</div>
        </div>
      )}

      {q.type === 'image_choice' && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--inbx-border)' }}>
          <label className="inbx-editor-label">Картинки-варианты</label>
          {(q.image_options ?? []).map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <AssetUpload
                  accept="image/*"
                  value={o.image_url}
                  onChange={(url) =>
                    onChange({
                      image_options: (q.image_options ?? []).map((x, j) =>
                        j === i ? { ...x, image_url: url ?? '' } : x,
                      ),
                    })
                  }
                />
                <input
                  className="inbx-editor-input"
                  placeholder="Подпись"
                  value={o.label ?? ''}
                  style={{ marginTop: 4 }}
                  onChange={(e) =>
                    onChange({
                      image_options: (q.image_options ?? []).map((x, j) =>
                        j === i ? { ...x, label: e.target.value, value: x.value || e.target.value } : x,
                      ),
                    })
                  }
                />
              </div>
              <button
                type="button"
                className="inbx-btn inbx-btn--danger"
                onClick={() =>
                  onChange({ image_options: (q.image_options ?? []).filter((_, j) => j !== i) })
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="inbx-btn"
            onClick={() =>
              onChange({
                image_options: [
                  ...(q.image_options ?? []),
                  { value: `opt_${Date.now()}`, image_url: '', label: '' },
                ],
              })
            }
          >
            + Картинка
          </button>
        </div>
      )}
    </div>
  );
}
